import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, ArrowRight, Loader2, Wand2 } from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { formatUSD } from "@/lib/constants";
import { useTranslation } from "react-i18next";

const EXAMPLES = ["advisor.ex1", "advisor.ex2", "advisor.ex3"];

export default function AIRecommender() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [desc, setDesc] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const recommend = async () => {
    if (desc.trim().length < 12) {
      toast.error(t("advisor.tooShort"));
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const res = await api.post("/ai/recommend", { description: desc });
      setResult(res.data);
    } catch {
      toast.error(t("advisor.unavailable"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <section className="relative grain">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 pt-32 pb-16">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-orange-700 border border-orange-200 bg-orange-50 rounded-full px-3 py-1">
              <Sparkles className="h-3 w-3" /> {t("advisor.kicker")}
            </span>
            <h1 className="mt-6 font-heading text-4xl sm:text-5xl tracking-tight text-foreground">{t("advisor.h1")}</h1>
            <p className="mt-4 text-muted-foreground">{t("advisor.lead")}</p>
          </motion.div>

          <div className="mt-8 rounded-2xl border border-border bg-card p-6 card-soft">
            <Textarea
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              placeholder={t("advisor.placeholder")}
              className="bg-background border-border min-h-[130px] text-base"
              data-testid="advisor-input"
            />
            <div className="mt-3 flex flex-wrap gap-2">
              {EXAMPLES.map((k) => { const ex = t(k); return (
                <button
                  key={k}
                  onClick={() => setDesc(ex)}
                  className="text-xs text-muted-foreground border border-border rounded-full px-3 py-1 hover:border-orange-300 hover:text-foreground transition-colors"
                  data-testid="advisor-example"
                >
                  {ex.length > 44 ? ex.slice(0, 44) + "…" : ex}
                </button>
              ); })}
            </div>
            <Button onClick={recommend} disabled={loading} className="mt-5 w-full rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 h-11" data-testid="advisor-submit">
              {loading ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t("advisor.analysing")}</>) : (<><Wand2 className="h-4 w-4 mr-2" /> {t("advisor.submit")}</>)}
            </Button>
          </div>

          <AnimatePresence>
            {result && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="mt-8" data-testid="advisor-result">
                <div className="rounded-2xl border border-orange-300 bg-card p-6 ring-1 ring-orange-200 card-soft">
                  <p className="text-xs uppercase tracking-[0.2em] text-orange-700">{t("advisor.recommended")}</p>
                  <div className="mt-3 flex items-start justify-between gap-4 flex-wrap">
                    <div>
                      <h2 className="font-heading text-3xl tracking-tight text-foreground">{result.recommended.name}</h2>
                      <p className="mt-1 font-mono text-orange-700">{formatUSD(result.recommended.price)} · {result.recommended.timeline}</p>
                    </div>
                    <Button onClick={() => navigate(`/start?pkg=${result.recommended.id}`)} className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90" data-testid="advisor-start-recommended">
                      {t("advisor.startThis")} <ArrowRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                  <p className="mt-4 text-slate-700 leading-relaxed">{result.reasoning}</p>
                </div>

                {result.alternatives?.length > 0 && (
                  <div className="mt-5">
                    <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">{t("advisor.alsoConsider")}</p>
                    <div className="grid sm:grid-cols-2 gap-4">
                      {result.alternatives.map((alt) => (
                        <button key={alt.id} onClick={() => navigate(`/start?pkg=${alt.id}`)} className="text-left rounded-xl border border-border bg-card p-4 card-soft hover:border-orange-300 transition-colors" data-testid={`advisor-alt-${alt.id}`}>
                          <p className="font-heading text-lg text-foreground">{alt.name}</p>
                          <p className="mt-1 text-sm text-muted-foreground">{formatUSD(alt.price)}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>
      <Footer />
    </div>
  );
}
